import { Component, EventEmitter, inject, Input, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MtxGridColumn, MtxGridModule, MtxGridRowClassFormatter } from '@ng-matero/extensions/grid';
import { ZipHistoryItem } from '@aipb/gulliver/dropbox/model';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { map, Observable, of } from 'rxjs';
import { intlFormatDistance } from 'date-fns';
import { ShortenPipe } from '@aipb/shared/ui/pipes';

@Component({
  selector: 'zm-zip-history-table',
  standalone: true,
  imports: [
    CommonModule,
    MtxGridModule,
    TranslocoPipe,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    ShortenPipe
  ],
  templateUrl: './zip-history-table.component.html',
  styleUrl: './zip-history-table.component.scss'
})
export class ZipHistoryTableComponent implements OnInit {
  @Input() items: ZipHistoryItem[] = [];
  @Input() loading: boolean = false;

  @Output() search = new EventEmitter<ZipHistoryItem>();
  @Output() copy = new EventEmitter<ZipHistoryItem>();
  @Output() open = new EventEmitter<ZipHistoryItem>();
  @Output() download = new EventEmitter<ZipHistoryItem>();
  @Output() detail = new EventEmitter<ZipHistoryItem>();
  @Output() rename = new EventEmitter<ZipHistoryItem>();
  @Output() filterByName = new EventEmitter<string>();
  @Output() delete = new EventEmitter<ZipHistoryItem>();

  @ViewChild('imageIndexTpl', { static: true }) imageIndexTpl!: TemplateRef<any>;
  @ViewChild('expiresAtTpl', { static: true }) expiresAtTpl!: TemplateRef<any>;
  @ViewChild('nameTpl', { static: true }) nameTpl!: TemplateRef<any>;
  columns: MtxGridColumn[] = [];
  rowClassFormatter: MtxGridRowClassFormatter = {
    expired: (data: ZipHistoryItem, index) => data.expired
  };
  filterByNameControl = new FormControl('');
  private transloco = inject(TranslocoService);

  ngOnInit() {
    this.filterByNameControl.valueChanges.subscribe((value) => {
      this.filterByName.emit(value);
    });

    this.columns = [
      {
        header: this.transloco.selectTranslate('zip_manager.zip_history_table.name'),
        field: 'name',
        cellTemplate: this.nameTpl,
        sortable: true
      },
      {
        header: this.transloco.selectTranslate('zip_manager.zip_history_table.created_at'),
        field: 'created_at',
        type: 'date',
        typeParameter: {
          format: 'dd.MM.yyyy HH:mm'
        },
        sortable: true
      },
      {
        header: this.transloco.selectTranslate('zip_manager.zip_history_table.expires_at'),
        field: 'expires_at',
        type: 'date',
        cellTemplate: this.expiresAtTpl,
        sortable: true
      },
      {
        header: this.transloco.selectTranslate('zip_manager.zip_history_table.quality'),
        field: 'quality',
        sortable: true
      },
      {
        header: this.transloco.selectTranslate('zip_manager.zip_history_table.download_count'),
        field: 'download_count',
        sortable: true,
        type: 'number'
      },
      {
        header: this.transloco.selectTranslate('zip_manager.zip_history_table.image_count'),
        field: 'image_count',
        sortable: true,
        type: 'number'
      },
      {
        header: this.transloco.selectTranslate('zip_manager.zip_history_table.index'),
        field: 'images',
        cellTemplate: this.imageIndexTpl
      },
      {
        header: this.transloco.selectTranslate('zip_manager.zip_history_table.actions'),
        field: 'action',
        width: '200px',
        pinned: 'right',
        right: '0px',
        type: 'button',
        buttons: [
          {
            type: 'icon',
            text: this.transloco.selectTranslate('zip_manager.zip_actions.search'),
            icon: 'search',
            tooltip: this.transloco.selectTranslate('zip_manager.zip_actions.search'),
            color: 'primary',
            click: (item: ZipHistoryItem) => {
              this.search.emit(item);
            }
          },
          {
            type: 'icon',
            text: this.transloco.selectTranslate('zip_manager.zip_actions.rename'),
            icon: 'edit_note',
            tooltip: this.transloco.selectTranslate('zip_manager.zip_actions.rename'),
            color: 'primary',
            click: (item: ZipHistoryItem) => {
              this.rename.emit(item);
            }
          },
          {
            type: 'icon',
            text: this.transloco.selectTranslate('zip_manager.zip_actions.copy'),
            icon: 'content_copy',
            tooltip: this.transloco.selectTranslate('zip_manager.zip_actions.copy'),
            color: 'primary',
            click: (item: ZipHistoryItem) => {
              this.copy.emit(item);
            }
          },
          {
            type: 'icon',
            text: this.transloco.selectTranslate('zip_manager.zip_actions.open'),
            icon: 'open_in_new',
            tooltip: this.transloco.selectTranslate('zip_manager.zip_actions.open'),
            color: 'primary',
            click: (item: ZipHistoryItem) => {
              this.open.emit(item);
            }
          },
          {
            type: 'icon',
            text: this.transloco.selectTranslate('zip_manager.zip_actions.download'),
            icon: 'cloud_download',
            tooltip: this.transloco.selectTranslate('zip_manager.zip_actions.download'),
            color: 'primary',
            disabled: (item: ZipHistoryItem) => item.expired,
            click: (item: ZipHistoryItem) => {
              this.download.emit(item);
            }
          },
          {
            type: 'icon',
            text: this.transloco.selectTranslate('zip_manager.zip_actions.delete'),
            icon: 'delete',
            tooltip: this.transloco.selectTranslate('zip_manager.zip_actions.delete'),
            color: 'warn',
            class: 'error-icon-button',
            click: (item: ZipHistoryItem) => {
              this.delete.emit(item);
            }
          }
        ]
      }
    ];
  }

  selectExpiration(expires_at: number): Observable<string> {
    if (!expires_at) return of('--');

    return this.transloco.langChanges$.pipe(
      map((lang) => {
        return intlFormatDistance(new Date(expires_at), new Date(), {
          locale: lang
        });
      })
    );
  }

  trackByName(index: number, item: any) {
    return item.id;
  }
}
